import * as browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { Prefs } from "../../alias/prefs";
import * as logger from "../../utilities/background";
import { isFilterMetadata } from "../../polyfills/background";
import { Timing } from "./timing.types";
import { isActiveOnDomain } from "../../../src/core/url/shared";
import { FilterOrigin } from "../../../src/filters/shared";
const configsStorageKey = "onpage_dialog_timing_configurations";
const knownConfigs = new Map();
async function getAllowlistingTime(tabId) {
    const allowlistingFilterTexts = await ewe.filters.getAllowingFilters(tabId);
    if (allowlistingFilterTexts.length === 0) {
        return null;
    }
    for (const filterText of allowlistingFilterTexts) {
        const metadata = await ewe.filters.getMetadata(filterText);
        if (!isFilterMetadata(metadata)) {
            continue;
        }
        if (metadata.origin !== FilterOrigin.web) {
            continue;
        }
        return metadata.created;
    }
    return null;
}
export function isTiming(candidate) {
    return typeof candidate === "string" && Object.values(Timing).includes(candidate);
}
function isTimingConfiguration(candidate) {
    return (candidate !== null &&
        typeof candidate === "object" &&
        "cooldownDuration" in candidate &&
        "maxDisplayCount" in candidate);
}
function isWithin(timestamp, minutes) {
    return timestamp < Date.now() - minutes * 60 * 1000;
}
function initializeConfigs() {
    const configs = Prefs.get(configsStorageKey);
    for (const [timing, config] of Object.entries(configs)) {
        if (!isTiming(timing) || !isTimingConfiguration(config)) {
            logger.warn("[onpage-dialog] Unknown timing configuration", timing);
            continue;
        }
        knownConfigs.set(timing, config);
    }
    logger.debug("[onpage-dialog]: Known timing configurations", knownConfigs);
}
export function shouldBeDismissed(dialog, stats, maxDisplayCountOverride) {
    if (typeof stats !== "object") {
        return false;
    }
    const config = knownConfigs.get(dialog.behavior.timing);
    if (typeof config !== "object") {
        logger.debug("[onpage-dialog]: Unknown timing");
        return true;
    }
    const maxDisplayCount = maxDisplayCountOverride !== null && maxDisplayCountOverride !== void 0 ? maxDisplayCountOverride : config.maxDisplayCount;
    if (maxDisplayCount === 0) {
        return false;
    }
    logger.debug("[onpage-dialog]: Display count", `${stats.displayCount}/${maxDisplayCount}`);
    return stats.displayCount >= maxDisplayCount;
}
async function shouldBeShownForAfterWebAllowlisting(timing, tab, stats) {
    var _a;
    const tabId = (_a = tab.id) !== null && _a !== void 0 ? _a : browser.tabs.TAB_ID_NONE;
    const config = knownConfigs.get(timing);
    if (typeof config !== "object") {
        logger.debug("[onpage-dialog]: Unknown timing");
        return false;
    }
    const allowlistingTime = await getAllowlistingTime(tabId);
    if (allowlistingTime === null) {
        logger.debug("[onpage-dialog]: Not allowlisted");
        return false;
    }
    if (typeof config.maxAllowlistingDelay === "number" &&
        isWithin(allowlistingTime, config.maxAllowlistingDelay)) {
        logger.debug("[onpage-dialog]: Allowlisted too long ago");
        return false;
    }
    if (typeof config.minAllowlistingDelay === "number" &&
        !isWithin(allowlistingTime, config.minAllowlistingDelay)) {
        logger.debug("[onpage-dialog]: Allowlisted too recently");
        return false;
    }
    if (!isWithin(stats.lastDisplayTime, config.cooldownDuration * 60)) {
        logger.debug("[onpage-dialog]: Dialog shown too recently");
        return false;
    }
    return true;
}
export async function shouldBeShown(tab, dialog, stats) {
    var _a;
    const { domainList, timing } = dialog.behavior;
    const isWebAllowlistingRelated = [
        Timing.afterWebAllowlisting,
        Timing.revisitWebAllowlisted,
    ].includes(timing);
    if (isWebAllowlistingRelated &&
        !(await shouldBeShownForAfterWebAllowlisting(timing, tab, stats))) {
        return false;
    }
    return isActiveOnDomain((_a = tab.url) !== null && _a !== void 0 ? _a : "", domainList);
}
export async function start() {
    await Prefs.untilLoaded;
    initializeConfigs();
    Prefs.on(configsStorageKey, () => {
        initializeConfigs();
    });
}
